/**
 * PR-H — BEDIT dashed frame.
 *
 * When the user is editing a block in-place (BEDIT command), AutoCAD
 * draws a yellow dashed rectangular frame around the block's bounding
 * box to make the edit boundary obvious. We mirror that contract.
 *
 * # Mesh strategy
 *
 * One persistent `THREE.LineSegments` with 8 vertices (4 dashed
 * segments forming a rectangle). On `setBounds(bounds, padding)` we
 * rewrite the position attribute in-place; the dashed material's
 * `linewidth` is conceptually `1.5 / cameraZoom` but Three.js
 * `LineBasicMaterial.linewidth` doesn't honour values > 1 in WebGL,
 * so we use a `LineSegments2`-backed implementation when fat lines
 * are needed.
 *
 * For PR-H we use plain `LineDashedMaterial` (1 px) because:
 *   - The BEDIT frame is not a widget the user interacts with
 *     directly; 1 px is sufficient visibility
 *   - LineSegments2 fat lines would require a `LineGeometry`
 *     redesign + linewidth uniform (already in flight elsewhere in
 *     the file), and the visual delta at typical zooms is small.
 *
 * The plan calls out `1.5 / cameraZoom` line width as a target — that
 * is the *visual width on screen*, not the world width. With a 1 px
 * baseline we are a hair thinner than spec; PR-H follow-up can swap
 * the implementation to LineSegments2 if the tester flags it.
 */

import * as THREE from "three";

/** AutoCAD BEDIT frame yellow. */
export const BEDIT_FRAME_COLOR = "#f1c40f";

export interface BeditFrameBounds {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/**
 * Frame handle. The caller owns mounting `mesh` into the scene; per
 * BEDIT entry/exit (and per frame while panning) the caller calls
 * `setBounds(bounds, padding, cameraZoom)` or `setHidden()`.
 *
 * `cameraZoom` is captured to drive the dash period — we want
 * dashes that read at ~6 CSS pixels regardless of zoom, so we scale
 * the dash size inversely with zoom on every update.
 */
export interface BeditFrameHandle {
  mesh: THREE.LineSegments;
  setBounds(
    bounds: BeditFrameBounds,
    paddingWorld: number,
    cameraZoom: number,
  ): void;
  setHidden(): void;
  dispose(): void;
}

/**
 * Build the BEDIT frame (4 dashed line segments). Position attribute
 * starts at zero; `setBounds` writes the actual rectangle each call.
 */
export function createBeditFrame(): BeditFrameHandle {
  const geometry = new THREE.BufferGeometry();
  const positions = new Float32Array(8 * 3);
  geometry.setAttribute(
    "position",
    new THREE.BufferAttribute(positions, 3),
  );

  const material = new THREE.LineDashedMaterial({
    color: new THREE.Color(BEDIT_FRAME_COLOR),
    dashSize: 5,
    gapSize: 5,
    transparent: false,
    depthTest: false,
    depthWrite: false,
  });

  const mesh = new THREE.LineSegments(geometry, material);
  mesh.userData = { kind: "bedit-frame" };
  mesh.visible = false;
  // Renders before scene contents but after the grid (grid is -100).
  mesh.renderOrder = -50;
  mesh.frustumCulled = false;

  const positionAttr = geometry.attributes.position as THREE.BufferAttribute;

  return {
    mesh,
    setBounds(bounds, paddingWorld, cameraZoom) {
      const arr = positionAttr.array as Float32Array;
      const left = bounds.left - paddingWorld;
      const right = bounds.right + paddingWorld;
      const top = bounds.top - paddingWorld;
      const bottom = bounds.bottom + paddingWorld;
      // 4 segments (8 vertices), traced CCW from bottom-left.
      // Bottom edge
      arr[0] = left; arr[1] = bottom; arr[2] = 0;
      arr[3] = right; arr[4] = bottom; arr[5] = 0;
      // Right edge
      arr[6] = right; arr[7] = bottom; arr[8] = 0;
      arr[9] = right; arr[10] = top; arr[11] = 0;
      // Top edge
      arr[12] = right; arr[13] = top; arr[14] = 0;
      arr[15] = left; arr[16] = top; arr[17] = 0;
      // Left edge
      arr[18] = left; arr[19] = top; arr[20] = 0;
      arr[21] = left; arr[22] = bottom; arr[23] = 0;
      positionAttr.needsUpdate = true;
      // Dash period stays ~5 px on screen at any zoom.
      const safeZoom = Math.max(cameraZoom, 1e-6);
      material.dashSize = 5 / safeZoom;
      material.gapSize = 5 / safeZoom;
      mesh.computeLineDistances();
      mesh.visible = true;
    },
    setHidden() {
      mesh.visible = false;
    },
    dispose() {
      geometry.dispose();
      material.dispose();
    },
  };
}
